import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  CardContent,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Button,
  CircularProgress,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import { FaBackward } from "react-icons/fa";
import { getData } from "../../../Webservice/Webservice";

function EmailDetails() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      try {
        const tokenValue = sessionStorage.getItem("token");
        const token = tokenValue ? `Bearer ${tokenValue}` : "";
        const response = await getData(`campaign/email/${campaignId}`, null, token);
        setData(response);
        setError(null);
      } catch (err) {
        setError("Failed to load campaign details.");
      } finally {
        setLoading(false);
      }
    };
    if (campaignId) fetchDetails();
  }, [campaignId]);

  const getStatusColor = (status) => {
    if (!status) return "default";
    switch (status.toLowerCase()) {
      case "completed":
        return "success";
      case "pending":
        return "warning";
      case "scheduled":
        return "info";
      case "draft":
        return "default";
      default:
        return "default";
    }
  };

  if (loading) {
    return (
      <Container sx={{ py: 8, display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <CircularProgress size={60} thickness={4} sx={{ color: "#3b82f6" }} />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 8 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2, textAlign: "center" }}>
          <Typography variant="h6" color="error" sx={{ fontWeight: 500 }}>
            {error}
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (!data) return null;

  const { campaign, logs } = data;

  return (
    <Container sx={{ py: 6, maxWidth: "lg" }}>
      <Paper
        elevation={3}
        sx={{
          boxShadow: "none",
          backgroundColor:"none",
          overflow: "hidden",
          transition: "all 0.3s ease",
        }}
      >
        <CardContent sx={{ p: { xs: 3, md: 5 } }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={4}>
            
            <Typography variant="h3" fontWeight={700} color="#0f172a" mb={1} sx={{ fontFamily: 'Mulish, sans-serif' }}>
              Email Campaign Details
            </Typography>
            <Button
              onClick={() => navigate(-1)}
              sx={{
                mr: 2,
                color: "#1f2937",
                bgcolor: "#f3f4f6",
                textTransform: "none",
                border:"1px solid #d1d5db",
                "&:hover": { bgcolor: "#e5e7eb"},
              }}
            >
              <FaBackward style={{ marginRight: 8 }} />
              Back
            </Button>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: "#111827", mb: 1 ,  fontFamily:'Mulish, sans-serif'}}>
                {campaign.name}
              </Typography>
              <Typography sx={{ color: "#6b7280", mb: 0.5, fontSize: "1.1rem" ,  fontFamily:'Mulish, sans-serif'}}>
                <strong>Subject:</strong> {campaign.subject}
              </Typography>
              <Typography sx={{ color: "#6b7280", mb: 0.5, fontSize: "1.1rem" ,  fontFamily:'Mulish, sans-serif'}}>
                <strong>Created:</strong> {new Date(campaign.createdAt).toLocaleString()}
              </Typography>
              {campaign.scheduledAt && (
                <Typography sx={{ color: "#6b7280", mb: 0.5, fontSize: "1.1rem" ,  fontFamily:'Mulish, sans-serif'}}>
                  <strong>Scheduled:</strong> {new Date(campaign.scheduledAt).toLocaleString()}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  justifyContent: { xs: "flex-start", md: "flex-end" },
                  alignItems: "center"
                }}
              >
                <Chip
                  label={campaign.status}
                  color={getStatusColor(campaign.status)}
                  size="medium"
                  sx={{
                    fontWeight: 600,
                    borderRadius: "12px",
                    bgcolor: getStatusColor(campaign.status) === "default" ? "#e5e7eb" : undefined,
                    color: getStatusColor(campaign.status) === "default" ? "#374151" : undefined
                  }}
                />
                <Chip
                  label={`Total: ${campaign.stats?.total ?? 0}`}
                  size="medium"
                  sx={{ fontWeight: 600, borderRadius: "12px", bgcolor: "#e5e7eb", color: "#374151" }}
                />
                <Chip
                  label={`Pending: ${campaign.stats?.pending ?? 0}`}
                  size="medium"
                  sx={{ fontWeight: 600, borderRadius: "12px", bgcolor: "#fef3c7", color: "#92400e" }}
                />
                <Chip
                  label={`Sent: ${campaign.stats?.sent ?? 0}`}
                  size="medium"
                  sx={{ fontWeight: 600, borderRadius: "12px", bgcolor: "#d1fae5", color: "#065f46" }}
                />
                <Chip
                  label={`Failed: ${campaign.stats?.failed ?? 0}`}
                  size="medium"
                  sx={{ fontWeight: 600, borderRadius: "12px", bgcolor: "#fee2e2", color: "#991b1b" }}
                />
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 4, borderColor: "#e5e7eb" }} />

          <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: "#1f2937",  fontFamily:'Mulish, sans-serif' }}>
            Content
          </Typography>
          <Box
            sx={{
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              p: 3,
              background: "#f9fafb",
              "& img": { maxWidth: "100%", borderRadius: "8px" }
            }}
            dangerouslySetInnerHTML={{ __html: campaign.content || "No content available" }}
          />

          {Array.isArray(campaign.attachments) && campaign.attachments.length > 0 && (
            <>
              <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 700, color: "#1f2937",  fontFamily:'Mulish, sans-serif' }}>
                Attachments
              </Typography>
              <List sx={{ bgcolor: "#f9fafb", borderRadius: "12px", p: 2 }}>
                {campaign.attachments.map((att, idx) => (
                  <ListItem
                    key={idx}
                    sx={{
                      py: 1,
                      borderBottom: idx < campaign.attachments.length - 1 ? "1px solid #e5e7eb" : "none",
                    }}
                  >
                    <ListItemText
                      primary={att}
                      primaryTypographyProps={{ fontWeight: 500, color: "#374151" }}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}

          <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 700, color: "#1f2937",  fontFamily:'Mulish, sans-serif' }}>
            Delivery Logs
          </Typography>
          <TableContainer component={Paper} sx={{ bgcolor: "#f9fafb", borderRadius: "12px", p: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: "#1f2937", fontFamily:'Mulish, sans-serif' }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#1f2937",  fontFamily:'Mulish, sans-serif' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#1f2937" ,  fontFamily:'Mulish, sans-serif'}}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#1f2937",  fontFamily:'Mulish, sans-serif' }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(logs) && logs.length > 0 ? (
                  logs.map((l, index) => (
                    <TableRow key={l._id}>
                      <TableCell sx={{ color: "#374151",  fontFamily:'Mulish, sans-serif' }}>{index + 1}</TableCell>
                      <TableCell sx={{ color: "#374151" ,  fontFamily:'Mulish, sans-serif'}}>{l.customerId?.name ?? "N/A"}</TableCell>
                      <TableCell sx={{ color: "#374151",  fontFamily:'Mulish, sans-serif' }}>{l.email}</TableCell>
                      <TableCell sx={{ color: "#374151",  fontFamily:'Mulish, sans-serif' }}>{l.status}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ color: "#6b7280", textAlign: "center" }}>
                      No logs available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Paper>
    </Container>
  );
}

export default EmailDetails;